import React, { useState } from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { COMPETITORS, FEATURES, OUR_SOFTWARE, getScore } from './marketData';

function marketAvg(featureId) {
  const vals = COMPETITORS.map(c => c.scores[featureId]);
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function marketLeader(featureId) {
  let best = 0, name = '';
  for (const c of COMPETITORS) {
    if (c.scores[featureId] > best) { best = c.scores[featureId]; name = c.name; }
  }
  return { score: best, name };
}

const GapBadge = ({ gap }) => {
  if (gap > 0.15) return <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1"><TrendingUp size={10} />Leading</span>;
  if (gap < -0.15) return <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1"><TrendingDown size={10} />Lagging</span>;
  return <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1"><Minus size={10} />Matched</span>;
};

const RADAR_KEYS = [
  { key: 'client_mgmt', label: 'Clients' },
  { key: 'scheduling',  label: 'Scheduling' },
  { key: 'staff_mgmt',  label: 'Staff' },
  { key: 'ndis_billing',label: 'Billing' },
  { key: 'mobile',      label: 'Mobile' },
  { key: 'gps',         label: 'GPS' },
  { key: 'incidents',   label: 'Incidents' },
  { key: 'financial',   label: 'Finance' },
  { key: 'ai_features', label: 'AI' },
  { key: 'compliance',  label: 'Compliance' },
];

export default function GapAnalysis() {
  const [view, setView] = useState('gap'); // 'gap' | 'radar'

  const featureRows = FEATURES.map(f => {
    const ourScore = OUR_SOFTWARE.scores[f.id];
    const avg      = marketAvg(f.id);
    const gap      = ourScore - avg;
    const leader   = marketLeader(f.id);
    return { ...f, ourScore, avg, gap, leader };
  });

  const leading  = featureRows.filter(r => r.gap > 0.15).length;
  const lagging  = featureRows.filter(r => r.gap < -0.15).length;
  const matched  = featureRows.filter(r => Math.abs(r.gap) <= 0.15).length;

  const barData = featureRows.map(r => ({
    name:  r.label.replace(' & ', '\n& '),
    us:    Math.round(r.ourScore * 100),
    avg:   Math.round(r.avg * 100),
    gap:   Math.round(r.gap * 100),
  }));

  const radarData = RADAR_KEYS.map(({ key, label }) => ({
    feature: label,
    Rostery: Math.round(OUR_SOFTWARE.scores[key] * 100),
    'Market Avg': Math.round(marketAvg(key) * 100),
    'Best in Class': Math.round(marketLeader(key).score * 100),
  }));

  const ourTotalScore = getScore(OUR_SOFTWARE.scores);
  const mktAvgScore   = Math.round(
    COMPETITORS.reduce((s, c) => s + getScore(c.scores), 0) / COMPETITORS.length
  );

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Gap Analysis</h1>
        <p className="text-sm text-slate-500 mt-1">
          Rostery vs market average — 20 features · 10 competitors benchmarked
        </p>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-indigo-50 border border-indigo-100 rounded-2xl px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500">Our Overall Score</p>
            <p className="text-2xl font-black text-indigo-600">{ourTotalScore}%</p>
          </div>
          <CheckCircle size={24} className="text-indigo-400" />
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500">Market Average</p>
            <p className="text-2xl font-black text-slate-600">{mktAvgScore}%</p>
          </div>
          <Minus size={24} className="text-slate-400" />
        </div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-2xl px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500">Features Leading</p>
            <p className="text-2xl font-black text-emerald-600">{leading}</p>
          </div>
          <TrendingUp size={24} className="text-emerald-400" />
        </div>
        <div className="bg-red-50 border border-red-100 rounded-2xl px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500">Features Lagging</p>
            <p className="text-2xl font-black text-red-600">{lagging}</p>
          </div>
          <TrendingDown size={24} className="text-red-400" />
        </div>
      </div>

      {/* Chart toggle */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-700">
            {view === 'gap' ? 'Feature Gap vs Market Average' : 'Radar Comparison'}
          </h2>
          <div className="flex gap-2">
            <button onClick={() => setView('gap')}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${view === 'gap' ? 'bg-indigo-600 text-white border-indigo-600' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
              Bar Chart
            </button>
            <button onClick={() => setView('radar')}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${view === 'radar' ? 'bg-indigo-600 text-white border-indigo-600' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
              Radar
            </button>
          </div>
        </div>

        <div className="p-5">
          {view === 'gap' ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={barData} layout="vertical" margin={{ top: 0, right: 40, left: 100, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={v => `${v}%`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} width={95} />
                <Tooltip formatter={(v, n) => [`${v}%`, n]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="us"  name="Rostery"    fill="#6366f1" radius={[0,3,3,0]} />
                <Bar dataKey="avg" name="Market Avg"   fill="#e2e8f0" radius={[0,3,3,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
                <PolarGrid stroke="#e2e8f0" />
                <PolarAngleAxis dataKey="feature" tick={{ fontSize: 10, fill: '#64748b' }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 9, fill: '#94a3b8' }} tickCount={4} />
                <Radar name="Rostery"    dataKey="Rostery"     stroke="#6366f1" fill="#6366f1" fillOpacity={0.3} strokeWidth={2} />
                <Radar name="Market Avg"  dataKey="Market Avg"    stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.1} strokeWidth={1.5} strokeDasharray="4 2" />
                <Radar name="Best in Class" dataKey="Best in Class" stroke="#10b981" fill="#10b981" fillOpacity={0.1} strokeWidth={1.5} strokeDasharray="4 2" />
                <Tooltip formatter={(v) => [`${v}%`]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </RadarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Feature-by-feature table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
        <div className="p-4 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-700">Feature-by-Feature Gap Detail</h2>
          <p className="text-xs text-slate-400 mt-0.5">Scores: 100% = full feature · 50% = partial · 0% = missing</p>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
              <th className="text-left px-4 py-3">Feature</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-right px-4 py-3">Rostery</th>
              <th className="text-right px-4 py-3">Market Avg</th>
              <th className="text-right px-4 py-3">Gap</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Market Leader</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {featureRows.map((r, i) => (
              <tr key={i} className={`hover:bg-slate-50 ${r.gap < -0.15 ? 'bg-red-50/30' : r.gap > 0.15 ? 'bg-emerald-50/20' : ''}`}>
                <td className="px-4 py-3 font-medium text-slate-700">{r.label}</td>
                <td className="px-4 py-3 text-slate-400 text-xs">{r.category}</td>
                <td className="px-4 py-3 text-right">
                  <span className={`font-bold ${r.ourScore === 1 ? 'text-emerald-600' : r.ourScore === 0.5 ? 'text-amber-600' : 'text-red-500'}`}>
                    {Math.round(r.ourScore * 100)}%
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-slate-500">{Math.round(r.avg * 100)}%</td>
                <td className="px-4 py-3 text-right">
                  <span className={`font-semibold ${r.gap > 0 ? 'text-emerald-600' : r.gap < 0 ? 'text-red-500' : 'text-slate-400'}`}>
                    {r.gap > 0 ? '+' : ''}{Math.round(r.gap * 100)}%
                  </span>
                </td>
                <td className="px-4 py-3"><GapBadge gap={r.gap} /></td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {r.ourScore >= r.leader.score ? (
                    <span className="text-emerald-600 font-medium flex items-center gap-1">
                      <CheckCircle size={11} />Us (tied/leading)
                    </span>
                  ) : (
                    <span>{r.leader.name}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Category breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Where we lead */}
        <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5">
          <h2 className="text-sm font-semibold text-emerald-700 mb-3 flex items-center gap-2">
            <CheckCircle size={15} />Competitive Advantages ({leading} features)
          </h2>
          <div className="space-y-2">
            {featureRows.filter(r => r.gap > 0.15).map((r, i) => (
              <div key={i} className="flex items-center justify-between bg-white rounded-xl px-3 py-2">
                <span className="text-xs font-medium text-slate-700">{r.label}</span>
                <span className="text-xs font-bold text-emerald-600">+{Math.round(r.gap * 100)}% vs avg</span>
              </div>
            ))}
          </div>
        </div>

        {/* Where we lag */}
        <div className="bg-red-50 border border-red-100 rounded-2xl p-5">
          <h2 className="text-sm font-semibold text-red-700 mb-3 flex items-center gap-2">
            <XCircle size={15} />Gaps to Close ({lagging} features)
          </h2>
          <div className="space-y-2">
            {featureRows.filter(r => r.gap < -0.15).map((r, i) => (
              <div key={i} className="flex items-center justify-between bg-white rounded-xl px-3 py-2">
                <span className="text-xs font-medium text-slate-700">{r.label}</span>
                <span className="text-xs font-bold text-red-600">{Math.round(r.gap * 100)}% vs avg</span>
              </div>
            ))}
            {lagging === 0 && (
              <p className="text-xs text-emerald-600 text-center py-4">No features significantly lagging — great position!</p>
            )}
          </div>
        </div>
      </div>

      {/* Overall verdict */}
      <div className="bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-200 rounded-2xl p-5">
        <h2 className="text-sm font-bold text-indigo-800 mb-2 flex items-center gap-2">
          <AlertCircle size={15} />Strategic Position Summary
        </h2>
        <p className="text-sm text-indigo-700 leading-relaxed">
          Rostery scores <strong>{ourTotalScore}%</strong> overall, compared to a market average of <strong>{mktAvgScore}%</strong>.
          We lead in <strong>{leading}</strong> feature categories (including AI, GPS, Job Board, Family Portal)
          and match or exceed most competitors. The key gaps are <strong>PRODA integration</strong>, <strong>Telehealth</strong>,
          and a <strong>native mobile app</strong> — the top 3 priorities for the improvement roadmap.
        </p>
      </div>
    </div>
  );
}
